// Settings — Office admin. Company settings as cards, each saved on its own.
//
//   PUT  /api/settings                 { settings: { key: value, … } }  one section at a time
//   POST /api/settings/upload-logo     { logo }                          a data: URL
//   POST /api/uploads/favicon          multipart, field name `favicon`     admin only
//
// The two uploads are deliberately different mechanisms, because the two
// endpoints are: the logo takes a JSON data: URL, the favicon takes multipart
// and writes the brand_favicon setting itself. server/routes/uploads.js
// filters on the FILENAME EXTENSION (jpg|jpeg|png|gif|ico|svg), not the MIME
// type, and caps at 5 MB — mirrored in apps/shared/uploads.js so a refusal
// names the real reason instead of surfacing "Only image files are allowed!".
//   POST /api/settings/test-email      { to }                            → { message, logs }
//   POST /api/settings/reinit-email    (no body)  → { success, message }  admin only
//
// **Saving email settings is two calls.** The server keeps one live mail
// transporter, built at boot from the settings rows, so writing the rows does
// nothing to outgoing mail until it is rebuilt — every message keeps going
// through the previous credentials until the process restarts. `PUT /settings`
// then `POST /settings/reinit-email`, the second non-blocking but never silent
// (#188). See `reinitSucceeded` for why the status code is not enough.
//
// The Stripe keys live in their own card (./StripeSettings.js) because they are
// not `settings` rows at all — they are a stripe_settings table behind
// /api/invoices/stripe/settings, admin-only at the route.
//
// Values come from useSettings() (App.js) — the same object the classic app
// reads — and refetch() after every save so the rest of the bundle sees the
// change. Admin only: everyone else gets an EmptyState, and the server
// refuses them anyway (checkRole('admin') on every write).
//
// The brand colour picker shows the CURRENT value from settings. It never
// hardcodes one — there is no hex literal in this file.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../../App';
import { useRole } from '../../../hooks/usePermission';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put, post, request, serverSentence } from '../../shared/http';
import { rejectUpload } from '../../shared/uploads';
import { PRESETS } from '../../../ui';
import { FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, EmptyState } from '../../../ui/fw';
import AddressField from '../../shared/AddressField';
import { DAY_NAMES } from '../../../utils/weekUtils';
import { useAction, PageHeader, Panel, FormError, FormRow } from '../records/OfficePage';
import { StripeSection } from './StripeSettings';
import './admin.css';


// The rules server/routes/uploads.js actually enforces now live in
// apps/shared/uploads.js — the profile photo needed the same ones, and two
// copies of one server file's behaviour is how the two drift apart. Re-exported
// so OfficeSettings.test.js keeps importing it from here.
export { rejectUpload };

const TIMEZONES = [
  ['America/New_York', 'Eastern (ET)'],
  ['America/Chicago', 'Central (CT)'],
  ['America/Denver', 'Mountain (MT)'],
  ['America/Phoenix', 'Arizona (no DST)'],
  ['America/Los_Angeles', 'Pacific (PT)'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
];

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

function pick(settings, keys) {
  const out = {};
  keys.forEach((k) => { out[k] = settings && settings[k] !== undefined && settings[k] !== null ? String(settings[k]) : ''; });
  return out;
}

/**
 * One section's form: seeded from settings, re-seeded whenever settings change
 * and nothing has been typed, saved with PUT /settings { settings }.
 */
function useSection(keys) {
  const { settings, refetch } = useSettings();
  const [form, setForm] = useState(() => pick(settings, keys));
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const { busy, error, run } = useAction();
  const keyList = keys.join(',');

  useEffect(() => {
    if (!dirty) setForm(pick(settings, keyList.split(',')));
  }, [settings, dirty, keyList]);

  const set = useCallback((k) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    setDirty(true);
    setSaved(false);
  }, []);

  const save = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const ok = await run(async () => {
      await put('/settings', { settings: form });
      if (refetch) await refetch();
    }, 'Could not save these settings.');
    if (ok) { setDirty(false); setSaved(true); }
    return ok;
  }, [form, run, refetch]);

  return { form, set, save, busy, error, dirty, saved };
}

function SaveRow({ busy, dirty, saved, label = 'Save' }) {
  return (
    <div className="or-actions">
      <FwButton type="submit" loading={busy} disabled={!dirty}>{label}</FwButton>
      {saved && !dirty ? <span className="or-muted">Saved.</span> : null}
    </div>
  );
}

function BrandingSection() {
  const { refetch } = useSettings();
  const s = useSection(['brand_name', 'brand_short', 'brand_color', 'brand_logo', 'brand_favicon']);
  const upload = useAction();
  const fav = useAction();

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    // A real <input type="file"> keeps its selection and fires NO change event
    // when the file chosen is the one it already holds. Every one of the four
    // ways this handler can end therefore has to clear it, or an admin whose
    // upload failed cannot retry that file at all — the avatar/logo bug PR
    // #518 fixed in the classic screens, reproduced here in code written
    // from scratch (docs/AGENT_LEARNINGS.md: a rewrite resets every defect
    // family to zero occurrences). Cleared first, since the File is already
    // captured and the rest of this function may await.
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) { upload.setError('Logo must be under 2 MB.'); return; }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error('Could not read the file.'));
      r.readAsDataURL(file);
    }).catch((err) => { upload.setError(err.message); return null; });
    if (!dataUrl) return;
    await upload.run(async () => {
      await post('/settings/upload-logo', { logo: dataUrl });
      if (refetch) await refetch();
    }, 'Could not upload the logo.');
  };

  // The favicon is multipart, not a data: URL, and the handler writes the
  // brand_favicon setting itself — so nothing here saves the form afterwards.
  const onFavicon = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const refusal = rejectUpload(file);
    if (refusal) { fav.setError(refusal); return; }
    await fav.run(async () => {
      const form = new FormData();
      form.append('favicon', file);
      try {
        await request('/uploads/favicon', { method: 'POST', body: form });
      } catch (err) {
        throw new Error(serverSentence(err, 'Could not upload the favicon.'));
      }
      // useSection re-syncs its form from settings whenever they change and
      // the form is not dirty, so refetch is enough to show the new icon. If
      // the admin has unsaved edits in this card the preview waits until they
      // save — which is right: re-syncing would discard what they typed.
      if (refetch) await refetch();
    }, 'Could not upload the favicon.');
  };

  return (
    <Panel title="Branding">
      <form className="or-form" onSubmit={s.save} noValidate>
        <FormError error={s.error || upload.error} />
        <FormRow>
          <FwField label="Company name" htmlFor="or-set-brand-name"><FwInput value={s.form.brand_name} onChange={s.set('brand_name')} /></FwField>
          <FwField label="Short name" htmlFor="or-set-brand-short" hint="Used where space is tight."><FwInput value={s.form.brand_short} onChange={s.set('brand_short')} maxLength={12} /></FwField>
        </FormRow>
        <FwField label="Brand colour" htmlFor="or-set-brand-color" hint="The accent the classic app and documents use.">
          <div className="or-color" id="or-set-brand-color-box">
            <input
              type="color"
              className="or-color__swatch"
              aria-label="Brand colour swatch"
              value={s.form.brand_color}
              onChange={s.set('brand_color')}
            />
            <FwInput id="or-set-brand-color" value={s.form.brand_color} onChange={s.set('brand_color')} placeholder="Current brand colour" />
          </div>
        </FwField>
        <FwField label="Logo" htmlFor="or-set-brand-logo-file" hint="Upload an image under 2 MB, or paste a URL.">
          {/* The wrapper carries its own id because FwField clones its first
              child and sets `id = htmlFor` on anything that lacks one — which
              put the file input's id on this div, gave the page two elements
              with one id, and pointed the <label> at a div. */}
          <div className="or-logo" id="or-set-brand-logo-box">
            {s.form.brand_logo ? <img className="or-logo__img" src={s.form.brand_logo} alt="Current logo" /> : null}
            <input id="or-set-brand-logo-file" className="or-file" type="file" accept="image/*" onChange={onFile} disabled={upload.busy} aria-label="Upload logo" />
          </div>
        </FwField>
        <FwInput id="or-set-brand-logo" value={s.form.brand_logo} onChange={s.set('brand_logo')} placeholder="https://…/logo.png" aria-label="Logo URL" />
        <FwField
          label="Favicon"
          htmlFor="or-set-favicon-file"
          hint="The tab icon, and the one the login page uses. Under 5 MB, one of jpg, jpeg, png, gif, ico, svg."
          error={fav.error || undefined}
        >
          <div className="or-logo" id="or-set-favicon-box">
            {s.form.brand_favicon
              ? <img className="or-logo__img or-logo__img--fav" src={s.form.brand_favicon} alt="Current favicon" />
              : null}
            <input
              id="or-set-favicon-file"
              className="or-file"
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.ico,.svg,image/*"
              onChange={onFavicon}
              disabled={fav.busy}
              aria-label="Upload favicon"
            />
          </div>
        </FwField>
        <SaveRow busy={s.busy} dirty={s.dirty} saved={s.saved} label="Save branding" />
      </form>
    </Panel>
  );
}

function CompanySection() {
  const s = useSection(['company_address', 'company_phone', 'company_email', 'default_timezone', 'start_of_week', 'default_tax_rate', 'google_maps_api_key']);
  const tzOptions = useMemo(() => {
    const list = [...TIMEZONES];
    if (s.form.default_timezone && !list.some(([v]) => v === s.form.default_timezone)) list.unshift([s.form.default_timezone, s.form.default_timezone]);
    return list;
  }, [s.form.default_timezone]);
  return (
    <Panel title="Company">
      <form className="or-form" onSubmit={s.save} noValidate>
        <FormError error={s.error} />
        {/* `set(k)` takes an event OR a bare value, so onPlace can hand it the
            formatted string directly. Suggestions here need the key that is
            edited four fields below — so on a fresh install this is a plain
            text box, and it stays one until someone saves a key and reloads
            (SettingsProvider fetches settings once per page). */}
        <AddressField label="Address" htmlFor="or-set-co-address" value={s.form.company_address} onChange={s.set('company_address')} onPlace={(p) => s.set('company_address')(p.formatted || p.street)} />
        <FormRow>
          <FwField label="Phone" htmlFor="or-set-co-phone"><FwInput type="tel" value={s.form.company_phone} onChange={s.set('company_phone')} /></FwField>
          <FwField label="Email" htmlFor="or-set-co-email"><FwInput type="email" value={s.form.company_email} onChange={s.set('company_email')} /></FwField>
        </FormRow>
        <FormRow>
          <FwField label="Timezone" htmlFor="or-set-co-tz" hint="Every week boundary and payroll period is computed in this zone.">
            <FwSelect value={s.form.default_timezone} onChange={s.set('default_timezone')}>
              {tzOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FwSelect>
          </FwField>
          <FwField label="Week starts on" htmlFor="or-set-co-sow">
            <FwSelect value={s.form.start_of_week} onChange={s.set('start_of_week')}>
              {DAY_NAMES.map((d, i) => <option key={d} value={String(i)}>{d}</option>)}
            </FwSelect>
          </FwField>
        </FormRow>
        <FwField label="Default sales tax (%)" htmlFor="or-set-co-tax" hint="Clients without their own rate inherit this. Blank means no company rate, which is not the same as 0.">
          <FwInput type="number" min="0" max="100" step="0.01" inputMode="decimal" value={s.form.default_tax_rate} onChange={s.set('default_tax_rate')} />
        </FwField>
        <FwField
          label="Google Maps API key"
          htmlFor="or-set-co-maps"
          hint="Powers address autocomplete. Restrict it by referrer in the Google console — a Maps browser key is sent to every visitor by design, so it is not a secret and is not masked here."
        >
          <FwInput value={s.form.google_maps_api_key} onChange={s.set('google_maps_api_key')} autoComplete="off" placeholder="AIza…" />
        </FwField>
        <SaveRow busy={s.busy} dirty={s.dirty} saved={s.saved} label="Save company" />
      </form>
    </Panel>
  );
}

function AppearanceSection() {
  const s = useSection(['appearance_preset']);
  return (
    <Panel title="Appearance">
      <form className="or-form" onSubmit={s.save} noValidate>
        <FormError error={s.error} />
        <FwField label="Preset" htmlFor="or-set-preset" hint="The classic app's palette. Light and dark are chosen per person.">
          <FwSelect value={PRESETS.includes(s.form.appearance_preset) ? s.form.appearance_preset : 'default'} onChange={s.set('appearance_preset')}>
            {PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
          </FwSelect>
        </FwField>
        <SaveRow busy={s.busy} dirty={s.dirty} saved={s.saved} label="Save appearance" />
      </form>
    </Panel>
  );
}

/**
 * The crew app's own policy. Owner instruction, 2026-09-10:
 * "a setting for admin/manager to toggle if worker is required to have gps on,
 * then that worker can't start the clock if gps isn't on."
 *
 * A <select> of exactly the two values server/routes/settings.js accepts, not
 * a free-text field and not a tick box saving true/false. Both PUT handlers
 * refuse anything but 'on' and 'off' (settingsRoutes.test.js), and a control
 * that can produce a third value turns that refusal into "Could not save these
 * settings" over a field an admin cannot see is wrong.
 *
 * The hint is load-bearing. This is the only row in the settings table whose
 * value can leave a crew member standing in a yard unable to record their
 * time, and an admin flipping something labelled "GPS required" has no way to
 * know that. So the card says it, in the words it will actually happen in.
 *
 * The section renders under `settings.update` — the CAPABILITY, not the role
 * literal — which is the gate maySeeSecretSettings() in routes/settings.js
 * already uses and the only one that can see a per-user `extra_permissions`
 * grant. Note that the WRITE path on both PUT handlers is still
 * `checkRole('admin')`, which this change deliberately does not touch:
 * widening it is a permissions change and belongs to a human (CLAUDE.md
 * rule 5). Until somebody does, a manager granted `settings.update` reaches
 * this screen's gate and is still refused by the server — the whole Settings
 * page is admin-only above this, so nobody meets that today.
 */
function CrewAppSection() {
  const s = useSection(['gps_required']);
  const value = s.form.gps_required === 'on' ? 'on' : 'off';
  return (
    <Panel title="Crew app">
      <form className="or-form" onSubmit={s.save} noValidate>
        <FormError error={s.error} />
        <FwField
          label="Require a location to clock in"
          htmlFor="or-set-gps-required"
          hint="Location is recorded on every clock in and out either way. Set this to On and a crew member whose phone will not give one cannot start the clock — they are told what to change and to call the office if it keeps failing. Leave it Off unless you need it: a phone inside a building or with no signal is a person who cannot record their time."
        >
          <FwSelect id="or-set-gps-required" value={value} onChange={s.set('gps_required')}>
            <option value="off">Off — record it when the phone offers it</option>
            <option value="on">On — no location, no clock-in</option>
          </FwSelect>
        </FwField>
        <SaveRow busy={s.busy} dirty={s.dirty} saved={s.saved} label="Save crew app" />
      </form>
    </Panel>
  );
}

/**
 * Reporting — the GitHub credentials behind the "Report a problem" button.
 *
 * Owner instruction, 2026-09-14: *"Under system I see report a problem is
 * configured, can we have the token be there as well, so we know it was added
 * and we admin can updated from the UI?"*
 *
 * ── Why this card is not a `useSection` like every other one ────────────────
 *
 * Because it CANNOT pre-fill. `useSection` seeds its form from useSettings(),
 * which is `GET /api/settings` — and that endpoint serves the token to nobody,
 * at any role (server/routes/settings.js, "WRITE-ONLY"). So the box starts
 * empty, always, and an empty box means "leave what is on file alone" rather
 * than "clear it" — the shape StripeSettings already uses, for the same
 * reason. The Clear button is how you actually empty it, because a
 * DESTRUCTIVE action must be something a person chose rather than something
 * they achieved by not typing.
 *
 * What is on file is read from `GET /api/admin/integrations`, which reports a
 * credential without disclosing one: present or not, which source, when it was
 * saved, and a four-character masked tail.
 *
 * ── The repo is a different thing and is treated like one ───────────────────
 *
 * `github_feedback_repo` is not a secret — server.js has always reported it on
 * the System screen, calling it "the useful half of the diagnosis" — so it
 * pre-fills from settings like any ordinary field.
 *
 * ── Two sources, and the screen says which ──────────────────────────────────
 *
 * A saved value WINS over the `GITHUB_FEEDBACK_*` environment variables
 * (server/utils/feedbackConfig.js). An admin on a box that has the env var set
 * must be able to see that that is what is in use, or "I saved it and nothing
 * changed" has no answer on any screen.
 */
export function feedbackTokenNote(feedback) {
  if (!feedback || typeof feedback !== 'object') return null;
  const t = feedback.token;
  if (!t || typeof t !== 'object') return null;
  if (t.unreadable) {
    return 'A token is saved here, but the server cannot read it — the encryption key has changed since it was saved. Save it again below.';
  }
  if (!t.present) return 'No token is on file. Reports are kept as notifications for admins and managers until one is added.';
  if (t.source === 'env') {
    return `A token is set on the server, in .env${t.hint ? ` (ending ${t.hint})` : ''}. Saving one below replaces it for this app — the file is not changed.`;
  }
  return `A token is on file${t.hint ? ` (ending ${t.hint})` : ''}. Leave the box empty to keep it.`;
}

function ReportingSection() {
  const { settings, refetch } = useSettings();
  const qc = useQueryClient();
  const [token, setToken] = useState('');
  const [repo, setRepo] = useState(null);      // null = not edited yet; use the saved value
  const [saved, setSaved] = useState(false);
  const { busy, error, run } = useAction();

  const q = useQuery({
    queryKey: ['office', 'integrations'],
    queryFn: () => get('/admin/integrations'),
    staleTime: 30000,
  });
  const feedback = q.data && q.data.integrations ? q.data.integrations.feedback : null;
  const note = feedbackTokenNote(feedback);

  const savedRepo = settings && settings.github_feedback_repo ? String(settings.github_feedback_repo) : '';
  const repoValue = repo === null ? savedRepo : repo;
  const dirty = token.trim() !== '' || (repo !== null && repo !== savedRepo);

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaved(false);
    const payload = {};
    // An untouched token box is OMITTED, not sent as ''. Sending '' would
    // clear a working credential because somebody fixed a typo in the repo.
    if (token.trim()) payload.github_feedback_token = token.trim();
    if (repo !== null) payload.github_feedback_repo = repo.trim();
    if (!Object.keys(payload).length) return;
    const ok = await run(async () => {
      await put('/settings', { settings: payload });
      if (refetch) await refetch();
      await qc.invalidateQueries({ queryKey: ['office', 'integrations'] });
    }, 'Could not save the reporting settings.');
    if (ok) { setToken(''); setRepo(null); setSaved(true); }
  };

  const clearToken = async () => {
    setSaved(false);
    const ok = await run(async () => {
      await put('/settings', { settings: { github_feedback_token: '' } });
      if (refetch) await refetch();
      await qc.invalidateQueries({ queryKey: ['office', 'integrations'] });
    }, 'Could not remove the token.');
    if (ok) { setToken(''); setSaved(true); }
  };

  return (
    <Panel title="Reporting">
      <form className="or-form" onSubmit={save} noValidate>
        <FormError error={error} />

        {/* A refused or unanswered read is NOT "no token". Same rule as the
            System screen and StripeSettings: the unconfigured state and a
            failure look identical if the failure is swallowed, and the wrong
            one of those sends an admin to re-enter a credential that is fine. */}
        {q.isPending ? <p className="or-muted" data-reporting="loading">Checking what is configured…</p> : null}
        {q.isError ? (
          <Notice tone="stop" data-reporting="load-failed">
            Could not read what is configured — {serverSentence(q.error, 'the server did not answer.')}
            {' '}This does <strong>not</strong> mean no token is on file; nothing is known either way until this loads.
          </Notice>
        ) : null}
        {note ? (
          <Notice
            tone={feedback && feedback.token && feedback.token.unreadable ? 'stop'
              : (feedback && feedback.token && feedback.token.present ? 'ok' : 'warn')}
            data-reporting="state"
          >
            {note}
          </Notice>
        ) : null}

        <FwField
          label="GitHub token"
          htmlFor="or-set-gh-token"
          hint="Encrypted on the server and never shown again — only the last four characters are ever displayed. Leave empty to keep the one on file."
        >
          <FwInput
            id="or-set-gh-token"
            type="password"
            value={token}
            onChange={(e) => { setToken(e.target.value); setSaved(false); }}
            autoComplete="new-password"
            placeholder="Paste a new token to replace the one on file"
          />
        </FwField>

        <FwField
          label="Repository"
          htmlFor="or-set-gh-repo"
          hint="Written as owner/repo. This is where a report becomes an issue; it is not a secret and is shown in full."
        >
          <FwInput
            id="or-set-gh-repo"
            value={repoValue}
            onChange={(e) => { setRepo(e.target.value); setSaved(false); }}
            autoComplete="off"
            placeholder="FullCircleProperty/fcp-fieldworks"
          />
        </FwField>

        <SaveRow busy={busy} dirty={dirty} saved={saved} label="Save reporting" />
      </form>

      {/* Separate from the form, and only offered when there is something to
          remove. Clearing hands the button back to whatever is in .env, if
          anything — said here, because "Remove" that silently re-enables an
          older credential is a surprise. */}
      {feedback && feedback.token && feedback.token.present && feedback.token.source === 'settings' ? (
        <div className="or-form">
          <p className="or-muted">
            Removing the saved token falls back to the server&apos;s <code>GITHUB_FEEDBACK_TOKEN</code>, if one is
            set there. If none is, reports are kept as notifications for admins and managers instead of opening issues.
          </p>
          <div className="or-actions">
            <FwButton variant="ghost" icon="trash" onClick={clearToken} loading={busy}>
              Remove the saved token
            </FwButton>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

const SMTP_KEYS = ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from_email', 'smtp_from_name'];
const GMAIL_KEYS = ['gmail_sa_client_email', 'gmail_sa_private_key', 'gmail_send_as', 'gmail_from_name'];

/**
 * What `POST /api/settings/reinit-email` actually said.
 *
 * The route (server/routes/settings.js:263-274) is `authMiddleware` +
 * `checkRole('admin')`, takes no body, and answers:
 *
 *   200 { success: true,  message: 'Email transporter re-initialized' }
 *   200 { success: false, message: 'Failed to initialize - check settings' }
 *   500 { error: 'Failed to re-initialize email' }
 *
 * **The middle one is the trap.** It is a 200, so `res.ok` is true and a
 * status-only check calls it a success — which is what the classic screen does
 * (client/src/components/Settings/Settings.js:136-141 reads `reinit.ok` and
 * nothing else), so the failure the warning exists for is the one it misses.
 * `reinitializeEmail` returns falsy when the settings are incomplete or the
 * transporter cannot be built, which is the ordinary case right after somebody
 * saves a half-filled SMTP form.
 *
 * So the ANSWER is read, not the status. A body with no `success` field at all
 * counts as success: that is a server on either side of this client answering
 * 200 to a call it accepted, and the field is what says no.
 */
export function reinitSucceeded(body) {
  if (!body || typeof body !== 'object') return true;
  return body.success !== false;
}

/**
 * The sentence to show after saving email settings.
 *
 * The two outcomes are separate on purpose (#188): the settings row IS written
 * even when the mail connection cannot be rebuilt, and reporting one failure as
 * the other sends an admin looking in the wrong place. The server holds a live
 * transporter, so until it is rebuilt every outgoing email keeps going through
 * the PREVIOUS credentials — silently, for as long as the process lives.
 */
export function emailSaveMessage(reloaded) {
  return reloaded
    ? 'Email settings saved, and the mail connection was reloaded.'
    : 'Email settings saved, but the mail connection could not be reloaded — outgoing email keeps using the previous credentials until this is fixed or the server restarts.';
}

function EmailSection() {
  const s = useSection(['email_provider', ...SMTP_KEYS, ...GMAIL_KEYS]);
  const [to, setTo] = useState('');
  const [result, setResult] = useState(null); // { message, logs }
  // null = not attempted since the last change; true/false = what the last
  // reinit answered. Never collapsed into one boolean with "not attempted",
  // because "we have not tried" and "it said no" are different things to tell
  // an admin.
  const [reloaded, setReloaded] = useState(null);
  const test = useAction();
  const reinit = useAction();
  const provider = s.form.email_provider || 'smtp';

  /**
   * Rebuild the server's transporter. Resolves true/false and never throws, so
   * chaining it onto a save cannot turn a successful save into a failed one.
   */
  const reloadMail = useCallback(async () => {
    let ok = false;
    await reinit.run(async () => {
      const res = await post('/settings/reinit-email', {});
      ok = reinitSucceeded(res);
      if (!ok) {
        // A 200 that says no. `run` sets `error` only when the promise
        // rejects, so the refusal has to be raised or it is never seen.
        throw new Error((res && res.message) || 'The mail connection could not be reloaded.');
      }
    }, 'The mail connection could not be reloaded.');
    setReloaded(ok);
    return ok;
  }, [reinit]);

  // Saving email settings is TWO calls, not one: the settings row, then the
  // live transporter. Deliberately non-blocking — a failed reload must not read
  // as a failed save — but never silent, which is the bug #188 fixed in the
  // classic screen and which this screen shipped without.
  const saveEmail = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setReloaded(null);
    const ok = await s.save();
    if (ok) await reloadMail();
  };

  const sendTest = async () => {
    setResult(null);
    await test.run(async () => {
      const res = await post('/settings/test-email', { to });
      setResult(res);
    }, 'The test email could not be sent.');
  };

  return (
    <Panel title="Email">
      <form className="or-form" onSubmit={saveEmail} noValidate>
        <FormError error={s.error} />
        <FwField label="Provider" htmlFor="or-set-email-provider">
          <FwSelect value={provider} onChange={s.set('email_provider')}>
            <option value="smtp">SMTP</option>
            <option value="gmail_api">Gmail API (service account)</option>
          </FwSelect>
        </FwField>
        {provider === 'gmail_api' ? (
          <>
            <FwField label="Service account email" htmlFor="or-set-gmail-client"><FwInput value={s.form.gmail_sa_client_email} onChange={s.set('gmail_sa_client_email')} /></FwField>
            <FwField label="Private key" htmlFor="or-set-gmail-key" hint="Pasted from the service account JSON. Stored server-side."><FwTextarea rows={4} value={s.form.gmail_sa_private_key} onChange={s.set('gmail_sa_private_key')} /></FwField>
            <FormRow>
              <FwField label="Send as" htmlFor="or-set-gmail-sendas"><FwInput type="email" value={s.form.gmail_send_as} onChange={s.set('gmail_send_as')} /></FwField>
              <FwField label="From name" htmlFor="or-set-gmail-from"><FwInput value={s.form.gmail_from_name} onChange={s.set('gmail_from_name')} /></FwField>
            </FormRow>
          </>
        ) : (
          <>
            <FormRow>
              <FwField label="SMTP host" htmlFor="or-set-smtp-host"><FwInput value={s.form.smtp_host} onChange={s.set('smtp_host')} /></FwField>
              <FwField label="Port" htmlFor="or-set-smtp-port"><FwInput inputMode="numeric" value={s.form.smtp_port} onChange={s.set('smtp_port')} /></FwField>
            </FormRow>
            <FormRow>
              <FwField label="Username" htmlFor="or-set-smtp-user"><FwInput value={s.form.smtp_user} onChange={s.set('smtp_user')} autoComplete="off" /></FwField>
              <FwField label="Password" htmlFor="or-set-smtp-pass"><FwInput type="password" value={s.form.smtp_password} onChange={s.set('smtp_password')} autoComplete="new-password" /></FwField>
            </FormRow>
            <FormRow>
              <FwField label="From email" htmlFor="or-set-smtp-from"><FwInput type="email" value={s.form.smtp_from_email} onChange={s.set('smtp_from_email')} /></FwField>
              <FwField label="From name" htmlFor="or-set-smtp-fromname"><FwInput value={s.form.smtp_from_name} onChange={s.set('smtp_from_name')} /></FwField>
            </FormRow>
          </>
        )}
        <SaveRow busy={s.busy || reinit.busy} dirty={s.dirty} saved={s.saved} label="Save email" />
        {s.saved && !s.dirty && reloaded !== null ? (
          <Notice tone={reloaded ? 'ok' : 'warn'} data-testid="or-set-reinit">{emailSaveMessage(reloaded)}</Notice>
        ) : null}
      </form>

      <div className="or-form">
        <FormError error={reinit.error} />
        <p className="or-muted">
          The server holds one live mail connection, built when it started. Saving above rebuilds
          it; use this if mail stopped going out after a change made somewhere else.
        </p>
        <div className="or-actions">
          <FwButton variant="ghost" icon="refresh" onClick={reloadMail} loading={reinit.busy}>
            Reload mail connection
          </FwButton>
        </div>
      </div>

      <div className="or-form">
        <FormError error={test.error} />
        {result ? <Notice tone="ok">{result.message || 'Test email sent.'}</Notice> : null}
        <FwField label="Send a test to" htmlFor="or-set-test-to" hint="Uses the saved settings above, so save first.">
          <FwInput type="email" value={to} onChange={(e) => setTo(e.target.value)} />
        </FwField>
        <div className="or-actions">
          <FwButton variant="ghost" icon="mail" onClick={sendTest} loading={test.busy} disabled={!to}>Send test email</FwButton>
        </div>
        {result && Array.isArray(result.logs) && result.logs.length > 0 ? <pre className="or-log">{result.logs.join('\n')}</pre> : null}
      </div>
    </Panel>
  );
}

// The Rebuild cutover card used to live here: four tick boxes deciding which
// roles got the new apps and which stayed on the classic one. There is one
// app now (docs/ONE_APP.md), so there is nothing to choose between — the
// setting it wrote, settings.rebuild_roles, is no longer read by anything.

/**
 * The five cards, and what each is actually FOR.
 *
 * MEASURED at 320x780 against the built app, 2026-09-11: this screen is
 * **4799px tall** — six phone screens of continuous form — with no way to
 * reach a section except scrolling past every section above it. That is the
 * "long form is not a screen" complaint, and the sections themselves were
 * already fine; getting to one was not.
 *
 * The blurb matters as much as the jump. "Branding" and "Company" do not tell
 * an admin which card holds the tax rate or which one holds the SMTP password,
 * so each says what is inside it in the words somebody would go looking with.
 *
 * A TABLE, not five hardcoded buttons, so the index cannot silently disagree
 * with the page: OfficeSettings.test.js walks it and fails if an entry names a
 * section the screen does not render, or if a section is rendered that the
 * index does not list. That is the same shape CLAUDE.md asks for everywhere
 * else here — "declare a contract once, and check it".
 */
export const SETTINGS_SECTIONS = [
  ['branding', 'Branding', 'Name, logo, favicon and the accent colour'],
  ['company', 'Company', 'Address, phone, timezone, week start and the default tax rate'],
  ['crew-app', 'Crew app', 'Whether a crew member needs a location to clock in'],
  ['appearance', 'Appearance', 'The palette everybody sees'],
  ['email', 'Email', 'How mail goes out, and a test send'],
  ['reporting', 'Reporting', 'The GitHub token and repo behind "Report a problem"'],
  ['payments', 'Payments', 'The Stripe keys invoices are paid through'],
];

/**
 * Jump to a card.
 *
 * `scrollIntoView` rather than an `<a href="#id">`: a bare hash anchor inside a
 * react-router app puts a hash on the URL that nothing routes on, and the
 * shell's scroll container is `main.fw-phone__body` rather than the document,
 * so the element — not the window — is the thing that has to be asked to
 * scroll. Guarded because jsdom does not implement it.
 */
function jumpTo(id) {
  const el = typeof document !== 'undefined' && document.getElementById(id);
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function SectionIndex() {
  return (
    <nav className="ad-index" aria-label="Settings sections" data-testid="settings-index">
      {SETTINGS_SECTIONS.map(([id, label, blurb]) => (
        <button
          type="button"
          key={id}
          className="ad-index__item"
          data-jump={id}
          onClick={() => jumpTo(`set-${id}`)}
        >
          <span className="ad-index__label">{label}</span>
          <span className="ad-index__blurb">{blurb}</span>
        </button>
      ))}
    </nav>
  );
}

export default function Settings() {
  const role = useRole();
  if (role !== 'admin') {
    return (
      <div className="or-page ad-page" data-screen="office-Settings">
        <PageHeader title="Settings" />
        <EmptyState icon="lock" title="Admins only">Company settings can only be changed by an administrator.</EmptyState>
      </div>
    );
  }
  return (
    <div className="or-page ad-page" data-screen="office-Settings">
      <PageHeader title="Settings" meta="Each card saves on its own." />
      <SectionIndex />
      {/* `Panel` takes no id and spreads no rest props (records/OfficePage.js),
          and it is shared by every Office screen, so the anchor is a wrapper
          here rather than a change to the primitive. */}
      <div id="set-branding"><BrandingSection /></div>
      <div id="set-company"><CompanySection /></div>
      {/* Rendered like every sibling, and NOT behind `useCan('settings',
          'update')` as it briefly was. Permissions here are role baseline UNION
          per-user extra_permissions (server/utils/permissions.js), so a grant
          can only ever be added — an admin cannot lack settings:update, and the
          screen has already refused everybody else above. A conditional section
          would also break the index contract outright: OfficeSettings.test.js
          asserts the anchor count EQUALS SETTINGS_SECTIONS.length, in both
          directions, so a card that sometimes renders is a card the index
          sometimes lies about. */}
      <div id="set-crew-app"><CrewAppSection /></div>
      <div id="set-appearance"><AppearanceSection /></div>
      <div id="set-email"><EmailSection /></div>
      <div id="set-reporting"><ReportingSection /></div>
      <div id="set-payments"><StripeSection /></div>
    </div>
  );
}
